// Mobile Darstellung der Interessenten-Listen: eine Karte je Kursart (die Orte
// stehen als Zeilen darin), Karten nach Alter sortiert und in Altersabschnitte
// geteilt. Reine Logik ohne React, damit sie isoliert testbar bleibt.

export type InterestGroupListLike = {
    courseType: {
        id: string
        name: string
        minAge?: number | null
        maxAge?: number | null
    }
    location: { name?: string | null; city?: string | null }
}

export type InterestGroup<T extends InterestGroupListLike> = {
    courseType: T["courseType"]
    lists: Array<T>
}

export type AgeBand<T extends InterestGroupListLike> = {
    key: string
    title: string
    chip: string
    groups: Array<InterestGroup<T>>
}

type CourseTypeAge = InterestGroupListLike["courseType"]

// Alter in Monaten. Grenze <= 0 oder leer = keine Grenze (wie renderAgeRange).
const NO_LIMIT = Number.MAX_SAFE_INTEGER
const limit = (months?: number | null): number | null => (months && months > 0 ? months : null)
// Ohne Untergrenze, aber mit Obergrenze („bis 3 J“) gilt ab Geburt; ganz ohne Alter ans Ende.
const minAgeKey = (courseType: CourseTypeAge): number => limit(courseType.minAge) ?? (limit(courseType.maxAge) ? 0 : NO_LIMIT)
const maxAgeKey = (courseType: CourseTypeAge): number => limit(courseType.maxAge) ?? NO_LIMIT

// Abschnitte nach Mindestalter der Kursart, Obergrenze exklusiv.
// ponytail: feste Grenzen für alle Schulen, je Schule einstellbar erst bei Bedarf.
const BANDS = [
    { key: "baby", title: "Bis 1 Jahr", chip: "bis 1 J", below: 12 },
    { key: "toddler", title: "1 bis 3 Jahre", chip: "1–3 J", below: 48 },
    { key: "preschool", title: "4 bis 5 Jahre", chip: "4–5 J", below: 72 },
    { key: "school", title: "6 bis 17 Jahre", chip: "6–17 J", below: 216 },
    {
        key: "adult",
        title: "Ab 18 Jahren",
        chip: "ab 18 J",
        below: NO_LIMIT,
    },
    {
        key: "all",
        title: "Alle Altersgruppen",
        chip: "ohne Alter",
        below: Infinity,
    },
]

/** Nach Mindestalter, dann Höchstalter, dann Name. Kursarten ohne Alter ans Ende. */
export function compareByAge(a: CourseTypeAge, b: CourseTypeAge): number {
    return minAgeKey(a) - minAgeKey(b) || maxAgeKey(a) - maxAgeKey(b) || a.name.localeCompare(b.name, "de")
}

const locationLabel = (list: InterestGroupListLike) => `${list.location.city ?? ""} ${list.location.name ?? ""}`

/** Fasst Listen je Kursart zusammen; Gruppen nach Alter, Orte nach Stadt und Name sortiert. */
export function groupInterestLists<T extends InterestGroupListLike>(lists: ReadonlyArray<T>): Array<InterestGroup<T>> {
    const byCourseType = new Map<string, InterestGroup<T>>()
    lists.forEach((list) => {
        const group: InterestGroup<T> = byCourseType.get(list.courseType.id) ?? {
            courseType: list.courseType,
            lists: [],
        }
        group.lists.push(list)
        byCourseType.set(list.courseType.id, group)
    })
    const groups = Array.from(byCourseType.values())
    groups.forEach((group) => group.lists.sort((a, b) => locationLabel(a).localeCompare(locationLabel(b), "de")))
    return groups.sort((a, b) => compareByAge(a.courseType, b.courseType))
}

/** Listen in Anzeigereihenfolge: Kursarten nach Alter, darin Orte nach Stadt und Name. */
export function sortInterestListsByAge<T extends InterestGroupListLike>(lists: ReadonlyArray<T>): Array<T> {
    return groupInterestLists(lists).reduce((all, group) => all.concat(group.lists), [] as Array<T>)
}

/** Teilt die Gruppen in Altersabschnitte; leere Abschnitte fallen weg. */
export function splitIntoAgeBands<T extends InterestGroupListLike>(groups: ReadonlyArray<InterestGroup<T>>): Array<AgeBand<T>> {
    return BANDS.map((band, index) => {
        const lowerBound = index === 0 ? 0 : BANDS[index - 1].below
        return {
            key: band.key,
            title: band.title,
            chip: band.chip,
            groups: groups.filter((group) => {
                const minAge = minAgeKey(group.courseType)
                return minAge >= lowerBound && minAge < band.below
            }),
        }
    }).filter((band) => band.groups.length > 0)
}
